import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getNotifications, markAllNotificationsAsRead, markNotificationAsRead } from "../../api/notifications";

export const useNotificationsInfinite = () => {
    return useInfiniteQuery({
        queryKey: ["notifications"],
        queryFn: async ({ pageParam = 1 }) => {
            const response = await getNotifications({ page: pageParam, limit: 10 });
            if (!response.status) {
                throw new Error(response.errorMsg || "Failed to fetch notifications");
            }
            return response.data;
        },
        getNextPageParam: (lastPage, allPages) => {
            const notifications = lastPage?.data?.notifications || [];
            if (notifications.length < 10) return undefined;
            return allPages.length + 1;
        },
        staleTime: Infinity, // Data is considered fresh individually
        refetchOnMount: false, // Do not refetch when component mounts
        refetchOnWindowFocus: false, // Do not refetch when window gains focus
        retry: 0,
        meta: { suppressGlobalLoader: true }
    });
};

export const useMarkAllNotificationsRead = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: markAllNotificationsAsRead,
        onSuccess: () => {
            queryClient.invalidateQueries(['notifications']);
        },
        meta: { suppressGlobalLoader: true }
    });
};

export const useMarkNotificationRead = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: markNotificationAsRead,
        onMutate: async (notificationId) => {
            await queryClient.cancelQueries(['notifications']);
            const previousNotifications = queryClient.getQueryData(['notifications']);

            queryClient.setQueryData(['notifications'], (old) => {
                if (!old) return old;

                let notificationFound = false;

                const newPages = old.pages.map((page) => {
                    const newNotifications = page.data.notifications.map((note) => {
                        if (note._id === notificationId && !note.isRead) {
                            notificationFound = true;
                            return { ...note, isRead: true };
                        }
                        return note;
                    });

                    return {
                        ...page,
                        data: { ...page.data, notifications: newNotifications }
                    };
                });

                if (notificationFound && newPages.length > 0) {
                    const firstPage = newPages[0];
                    if (firstPage.data.unreadCount > 0) {
                        newPages[0] = {
                            ...firstPage,
                            data: {
                                ...firstPage.data,
                                unreadCount: firstPage.data.unreadCount - 1
                            }
                        };
                    }
                }

                return { ...old, pages: newPages };
            });

            return { previousNotifications };
        },
        onError: (err, newTodo, context) => {
            if (context?.previousNotifications) {
                queryClient.setQueryData(['notifications'], context.previousNotifications);
            }
        },
        meta: { suppressGlobalLoader: true }
    });
};
